import { Link, useParams, useSearch } from '@tanstack/react-router'
import { ErrorState } from '@/components/status'
import { cn } from '@/lib/cn'
import { useLiveTopic } from '@/lib/live'
import { useProject } from './data'
import { RepoMissingBanner } from './RepoMissing'
import { Stages } from './Stages'
import { Tickets } from './Tickets'
import { ProjectSettingsPage } from './Settings'
import { SessionMain, SessionSidebar, useCurrentSession } from '@/features/sessions/Sessions'

export { prUrl, useProject } from './data'

type Tab = 'tickets' | 'stages' | 'settings'

/**
 * Project page (03 §5.6): the app header shows the name and the tabs
 * (ProjectTabs); below are the project's Claude sessions down the left, and two tabs: Tickets (every
 * Linear ticket by status, docs/ticketing.md) and Stages (the delivery lifecycle). An open session takes
 * the main area until a tab is picked again.
 */
export function ProjectOverviewPage() {
  const { project, section } = useParams({ strict: false }) as { project: string; section?: string }
  const search = useSearch({ strict: false }) as { tab?: string }
  useLiveTopic(`project:${project}`)
  const proj = useProject(project)
  const session = useCurrentSession(project)
  // Settings lives at /p/$project/settings/$section, shown as the third tab.
  const tab: Tab = section ? 'settings' : search.tab === 'stages' ? 'stages' : 'tickets'

  const p = proj.data
  if (proj.error && !p) return <ErrorState error={proj.error} retry={() => void proj.refetch()} />

  return (
    <div className="flex h-full flex-col">
      {p && <RepoMissingBanner project={p} />}
      <div className="flex min-h-0 flex-1">
        {!session && <SessionSidebar project={project} />}
        <main className="flex min-w-0 flex-1 flex-col">
          {/* A session fills the space (its dock sizes from it); the tabs scroll. */}
          <div className={cn('min-h-0 flex-1', session ? 'flex flex-col' : 'overflow-auto')}>
            {session ? <SessionMain project={project} session={session} /> : tab === 'settings' ? <ProjectSettingsPage embedded /> : tab === 'stages' ? <Stages project={project} /> : <Tickets project={project} />}
          </div>
        </main>
      </div>
    </div>
  )
}

/**
 * The project's tabs, in the app header beside its name: Tickets, Stages, CI
 * (only for a project that runs its own CI; the rest keep GitHub Actions) and
 * Settings.
 */
export function ProjectTabs({ project, tab, ci }: { project: string; tab: Tab | 'ci'; ci: boolean }) {
  return (
    <div role="tablist" aria-label="Project" className="flex items-center gap-0.5">
      <TabLink project={project} tab="tickets" on={tab === 'tickets'}>Tickets</TabLink>
      <TabLink project={project} tab="stages" on={tab === 'stages'}>Stages</TabLink>
      {ci && <Link role="tab" aria-selected={tab === 'ci'} to="/p/$project/ci" params={{ project }} className={tabClass(tab === 'ci')}>CI</Link>}
      <Link role="tab" aria-selected={tab === 'settings'} to="/p/$project/settings/$section" params={{ project, section: 'general' }} className={tabClass(tab === 'settings')}>Settings</Link>
    </div>
  )
}

const tabClass = (on: boolean) => cn('h-7 rounded-md px-2.5 text-sm leading-7 transition-colors', on ? 'bg-surface-3 text-fg' : 'text-fg-subtle hover:bg-hover hover:text-fg')

function TabLink({ project, tab, on, children }: { project: string; tab: Tab; on: boolean; children: React.ReactNode }) {
  return (
    <Link role="tab" aria-selected={on} to="/p/$project" params={{ project }} search={{ tab }} className={tabClass(on)}>
      {children}
    </Link>
  )
}
